import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { getCardSetupPaymentMethod } from "~/components/autopto/cardDialog/cardSetupReturnPath";
import {
  PaymentMethodLike,
  paymentMethodSignature,
} from "~/components/autopto/cardDialog/paymentMethodSignature";
import { useTeam } from "~/contexts/TeamProvider";
import { usePolling } from "~/hooks/usePolling";

// About 20 seconds of rechecks (3 s apart): then billing stops saying it's
// saving and gives the card button back (the same card entered again never
// shows a change), while the rechecks go on for their minute. The trial
// and the block screens hold the button for the whole minute.
const SAVING_RECHECKS = 7;

export type SavedCardStatus = "saving" | "slow" | null;

/**
 * Back from Stripe, rechecks billing's subscription until it shows another
 * payment method than the server had when the card dialog opened: the
 * webhook writes the card a few seconds later, and the SubscriptionsChannel
 * may miss it during the page load. A blocked team too: the block screens
 * watch the team, not the card. `paymentMethod` is undefined until the
 * subscription loads; the minute runs from the return all the same, so a
 * subscription that never loads doesn't hold the card button.
 */
export function useSavedCardRecheck(
  paymentMethod: PaymentMethodLike | undefined,
): SavedCardStatus {
  const { team } = useTeam();
  const queryClient = useQueryClient();
  const location = useLocation();
  const isAfterCardSetup = Boolean(location.state?.afterCardSetup);
  const teamId = team?.id;

  const [replaced, setReplaced] = useState<{
    paymentMethod: string | null;
    teamId: number;
  } | null>(null);
  const [rechecks, setRechecks] = useState(0);

  const shown =
    paymentMethod === undefined
      ? undefined
      : paymentMethodSignature(paymentMethod);
  const isWatching = replaced !== null && replaced.teamId === teamId;
  const isUnchanged = isWatching && shown === replaced.paymentMethod;

  // Not cancelling a refetch in flight: a load longer than a tick would
  // never land.
  const refetchSubscription = useCallback(() => {
    setRechecks((count) => count + 1);
    void queryClient.invalidateQueries(
      { queryKey: ["subscriptionPlans"] },
      { cancelRefetch: false },
    );
  }, [queryClient]);

  const { isPolling, startPolling } = usePolling(
    isWatching && (shown === undefined || isUnchanged),
    refetchSubscription,
  );

  // Read before the card dialog clears it: the page's effects run first.
  useEffect(() => {
    if (!isAfterCardSetup || teamId === undefined) return;

    const replacedPaymentMethod = getCardSetupPaymentMethod();

    if (replacedPaymentMethod !== undefined) {
      setReplaced({ paymentMethod: replacedPaymentMethod, teamId });
      setRechecks(0);
      startPolling();
    }
  }, [isAfterCardSetup, teamId, startPolling]);

  // Over once the new one is in, on another team, or when the minute ran
  // out (the same card, or a subscription that didn't load).
  useEffect(() => {
    if (replaced && !isPolling) setReplaced(null);
  }, [replaced, isPolling]);

  if (!isWatching || (shown !== undefined && !isUnchanged)) return null;

  return rechecks < SAVING_RECHECKS ? "saving" : "slow";
}
